/**
 * mock-chart-curve.ts — 0-dep monotone/Catmull-Rom curve builder for
 * `MockChart` (R4-P2 build brief §2.5, dossier §2.5, "Measured deps (P2)":
 * `toPath()` REMAINS THE DEFAULT even though `@visx/curve`'s
 * `curveMonotoneX` is now installed and gate-verified (0.65KB gz) — a
 * future lane may swap the point-to-path step for `curveMonotoneX` behind
 * this same `toPath(values, width, height)` signature without touching
 * `MockChart`'s call site. The tension constant below is FIXED and
 * deterministic — never randomized (no hand-jittered bezier control
 * points, per the dossier's explicit ban).
 *
 * Pure functions only — no React, no DOM — keeps `MockChart` a 0-client-JS
 * inline SVG (RSC-safe).
 */

type Point = readonly [x: number, y: number];

/** Catmull-Rom tension divisor; smaller = tighter curve. Fixed, not tunable
 * per-call — a single consistent "house curve" across every MockChart. */
const TENSION = 6;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Maps a normalized `[0,1]` value series onto evenly-spaced pixel points
 * across `width`, inverted so `1` sits near the top (small y) and `0` sits
 * near the bottom (large y), inset by `padY` top/bottom so a 1.5px stroke
 * never clips the viewBox edge.
 */
function mapPoints(
  values: readonly number[],
  width: number,
  height: number,
  padY: number
): Point[] {
  const n = values.length;
  const innerHeight = height - padY * 2;
  return values.map((value, i) => {
    const x = n === 1 ? width / 2 : (i / (n - 1)) * width;
    const y = padY + (1 - clamp01(value)) * innerHeight;
    return [x, y] as const;
  });
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

function catmullRomPath(points: readonly Point[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${round(points[0][0])},${round(points[0][1])}`;

  let d = `M${round(points[0][0])},${round(points[0][1])}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;

    const c1x = p1[0] + (p2[0] - p0[0]) / TENSION;
    const c1y = p1[1] + (p2[1] - p0[1]) / TENSION;
    const c2x = p2[0] - (p3[0] - p1[0]) / TENSION;
    const c2y = p2[1] - (p3[1] - p1[1]) / TENSION;

    d += ` C${round(c1x)},${round(c1y)} ${round(c2x)},${round(c2y)} ${round(p2[0])},${round(p2[1])}`;
  }
  return d;
}

/**
 * Smooth line `d` for one normalized `[0,1]` series, mapped onto a
 * `width` × `height` plot with `padY` vertical inset. Points are evenly
 * spaced across the full width — the DEFAULT curve for `MockChart`.
 */
export function toPath(values: readonly number[], width: number, height: number, padY = 8): string {
  return catmullRomPath(mapPoints(values, width, height, padY));
}

/**
 * Same smooth line, closed down to a flat `height - padY` baseline (and
 * back to the first point's x) — `MockChart`'s area-under-series fill
 * (dossier §2.5). Only `series[0]` gets an area fill; `series[1]` stays a
 * bare line.
 */
export function toAreaPath(
  values: readonly number[],
  width: number,
  height: number,
  padY = 8
): string {
  const points = mapPoints(values, width, height, padY);
  if (points.length === 0) return '';
  const line = catmullRomPath(points);
  const baselineY = round(height - padY);
  const first = points[0];
  const last = points[points.length - 1];
  return `${line} L${round(last[0])},${baselineY} L${round(first[0])},${baselineY} Z`;
}
